import React, { useCallback, useRef, useState } from 'react'
import {
  BUTTON_SIZE,
  DEFAULT_CORNER,
  Point,
  WidgetCorner,
  getNearestCorner,
  hasExceededDragThreshold,
  readStoredCorner,
  writeStoredCorner,
} from './position'

const readCornerSafe = (): WidgetCorner => {
  try {
    return readStoredCorner(window.localStorage)
  } catch {
    return DEFAULT_CORNER
  }
}

const writeCornerSafe = (corner: WidgetCorner): void => {
  try {
    writeStoredCorner(corner, window.localStorage)
  } catch {
    // private mode / quota
  }
}

const clampToViewport = (x: number, y: number): Point => ({
  x: Math.min(Math.max(0, x), window.innerWidth - BUTTON_SIZE),
  y: Math.min(Math.max(0, y), window.innerHeight - BUTTON_SIZE),
})

export const useDraggableCorner = (onDragStart: () => void) => {
  const [corner, setCorner] = useState<WidgetCorner>(readCornerSafe)
  const [dragPosition, setDragPosition] = useState<Point | null>(null)
  const onDragStartRef = useRef(onDragStart)
  onDragStartRef.current = onDragStart
  const dragRef = useRef({
    pointerId: null as number | null,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
    dragging: false,
    suppressClick: false,
  })

  const finishDrag = useCallback((target: HTMLButtonElement) => {
    const drag = dragRef.current
    if (!drag.dragging) {
      drag.pointerId = null
      return
    }

    const rect = target.getBoundingClientRect()
    const nextCorner = getNearestCorner(
      rect.left + rect.width / 2,
      rect.top + rect.height / 2,
      window.innerWidth,
      window.innerHeight,
    )

    drag.dragging = false
    drag.pointerId = null
    setDragPosition(null)
    setCorner(nextCorner)
    writeCornerSafe(nextCorner)
    document.body.style.userSelect = ''
    document.body.style.cursor = ''
  }, [])

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) {
      return
    }

    const rect = event.currentTarget.getBoundingClientRect()
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      dragging: false,
      suppressClick: false,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }, [])

  const onPointerMove = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current
    if (drag.pointerId !== event.pointerId) {
      return
    }

    if (!drag.dragging) {
      if (!hasExceededDragThreshold(
        { x: drag.startX, y: drag.startY },
        { x: event.clientX, y: event.clientY },
      )) {
        return
      }

      drag.dragging = true
      drag.suppressClick = true
      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'grabbing'
      onDragStartRef.current()
    }

    setDragPosition(clampToViewport(
      event.clientX - drag.offsetX,
      event.clientY - drag.offsetY,
    ))
  }, [])

  const onPointerUp = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (dragRef.current.pointerId !== event.pointerId) {
      return
    }

    finishDrag(event.currentTarget)

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }, [finishDrag])

  const consumeClickSuppression = useCallback(() => {
    if (!dragRef.current.suppressClick) {
      return false
    }

    dragRef.current.suppressClick = false
    return true
  }, [])

  return {
    corner,
    dragPosition,
    isDragging: dragPosition !== null,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onLostPointerCapture: onPointerUp,
    consumeClickSuppression,
  }
}
